import React, { useState } from "react";
import { Table, Tag, Button, Row, Tooltip, Typography, Image, Modal, message } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { GET_COUPONS, GET_CATEGORIES_SORTED, DELETE_COUPON } from "./requests";
import { couponTypes } from "../../types";
import TableTitle from "./table-title";
import CreateCoupon from "./components/createModal";
import EditCoupon from "./components/editModal";
import { Container } from "./elements";
import { renewalOptions } from "../../utils/constants";
import PriorityGroup from "./components/sortModal";
import CreateGroupCoupon from "./components/createGroupModal";

const { Text } = Typography;

const CouponsTable: React.FC = () => {
  const [coupon, setCoupon] = useState<typeof couponTypes.coupon>();
  const [nameFilter, setNameFilter] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [pageConfig, setPageConfig] = useState({
    page: 1,
    pageSize: 10,
  });
  const [showPriorityModal, setShowPriorityModal] = useState<boolean>(false);

  const [deleteCouponMutation] = useMutation(DELETE_COUPON)

  const { loading, data, refetch } = useQuery(GET_COUPONS, {
    variables: {
      params: pageConfig,
      name: nameFilter,
      all: true,
    },
  });

  const coupons = data?.coupons.results ?? [];

  const [loadSortedCategories, { data: sortCategories, refetch: refetchGroups }] = useLazyQuery(
    GET_CATEGORIES_SORTED,
    {
      variables: {
        params: { page: 1,
          pageSize: 100 },
      },
    },
  );

  const columns = [
    {
      title: "",
      dataIndex: "img",
      key: "img",
      width: "8%",
      render: (img: string) => {
        if (img && img !== "") {
          return <Image width={50} src={img} />;
        } else {
          return null;
        }
      },
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Antes/Despues",
      key: "comparePrice",
      render: (price) => (
        <div>
          <Tag color="red">
            <Text style={{ textDecoration: "line-through" }}>
              ${price?.comparePrice}
            </Text>{" "}
            MXN
          </Tag>
          <Tag color="green">
            <Text style={{ fontWeight: "800",
              color: "green" }}>
              ${price?.totalPrice}{" "}
            </Text>
            MXN
          </Tag>
        </div>
      ),
    },

    {
      title: "Límite",
      dataIndex: "limitPerClient",
      key: "limitPerClient",
      render: (limit) =>
        limit !== null ? (
          <Tag color="blue">{limit} Usos</Tag>
        ) : (
          <Tag color="red">Sin límite</Tag>
        ),
    },
    {
      title: "Recurrencia",
      key: "renewal",
      render: (row) => {
        return (
          <div>
            <Tag color={renewalOptions[row?.renewal].color}>
              {renewalOptions[row?.renewal].name}
            </Tag>
            <br />
            <Text style={{ fontWeight: "800",
              color: "grey" }}>
              {row?.expires
                ? new Date(row?.expires).toLocaleDateString()
                : "No aplica"}
            </Text>
          </div>
        );
      },
    },

    {
      title: "Acción",
      key: "action",
      width: "10%",
      render: (row: typeof couponTypes.coupon) => {
        return (
          <Row>
            <Tooltip placement="top" title="Editar">
              <Button
                shape="circle"
                type="primary"
                size="large"
                icon={<EditOutlined style={{ color: "white" }} />}
                style={{ margin: 5 }}
                onClick={() => {
                  setShowEditModal(true);
                  setCoupon(row);
                }}
              />
            </Tooltip>
            <Tooltip placement="top" title="Eliminar">
              <Button
                type="dashed"
                danger
                size="large"
                icon={<DeleteOutlined />}
                shape="circle"
                onClick={() => showDeleteConfirm(row)}
                style={{ margin: 5 }}
              />
            </Tooltip>
          </Row>
        );
      },
    },
  ];

  const showDeleteConfirm = (coupon: typeof couponTypes.coupon) => {
    Modal.confirm({
      title: "¿Estás seguro de eliminar este cupón?",
      content: `El cupón ${coupon.name} será eliminado.`,
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => handleDeleteCoupon(coupon.id),
    });
  };

  const handleDeleteCoupon = async (couponId: string) => {
    try {
      await deleteCouponMutation({
        variables: { id: couponId },
      });
      message.success("Cupón eliminado correctamente");
      await refetch();
    } catch (error) {
      console.log(error);
      message.error("Error al eliminar el cupón");
    }
  };

  return (
    <Container style={{ overflow: "scroll" }}>
      <Table
        title={() => (
          <TableTitle
            setNameFilter={setNameFilter}
            setShowCreateModal={setShowCreateModal}
            setShowPriorityModal={(value) => {
              setShowPriorityModal(value);
              if (value) {
                loadSortedCategories();
              }
            }}
            setShowCreateGroupModal={setShowCreateGroupModal}
          />
        )}
        loading={loading}
        dataSource={coupons}
        columns={columns}
        rowKey="id"
        pagination={{
          current: Number(pageConfig.page),
          defaultCurrent: pageConfig.page,
          pageSize: Number(pageConfig.pageSize),
          defaultPageSize: pageConfig.pageSize,
          total: data?.coupons?.info?.count,
          showTotal: (total) => <Text disabled>{`${total} resultados`}</Text>,
          onChange: (page, pageSize) => {
            setPageConfig({
              page,
              pageSize,
            });
          },
          showSizeChanger: true,
        }}
      />
      <CreateCoupon
        showModal={showCreateModal}
        setShowModal={setShowCreateModal}
        refetch={refetch}
      />
      <EditCoupon
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        coupon={coupon}
        refetch={refetch}
      />
      <PriorityGroup
        showModal={showPriorityModal}
        setShowModal={setShowPriorityModal}
        refetch={refetch}
        data={sortCategories}
        sortRefetch={loadSortedCategories}
      />
      <CreateGroupCoupon
        showGroupModal={showCreateGroupModal}
        setShowGroupModal={setShowCreateGroupModal}
        refetch={refetchGroups}
        isCreateView={true}
        selectedGroups={[]}
        setSelectedGroups={()=>{}}
      />
    </Container>
  );
};

export default CouponsTable;
